import { useCallback, useEffect, useRef, useState } from 'react';
import FocusLock from 'react-focus-lock';
import cx from 'classnames';
import { useMounted } from '../../../hooks/useMounted';
import { useOnOutsideMouseDown } from '../../../hooks/useOnOutsideMouseDown';
import { useSingleAndDoubleClick } from '../../../hooks/useSingleAndDoubleClick';
import { ContextMenu } from '../../ContextMenu';
import { TableRegions } from '../TableRegions';
import { getSelected } from './internal';
import tcn from '../../TreeTable/TreeTableNode.css.json';
import cn from '../Table.css.json';
import '../Table.css';
import { jsx as _jsx } from "react/jsx-runtime";
export const isCellControl = (e, column, isInteractiveControl, rowKey) => {
  const clickedElement = e.target;
  let current = clickedElement;
  let nonInteractive = false;
  if (isInteractiveControl) {
    return isInteractiveControl?.(current, column.key, rowKey);
  }
  if (column.containsInteractiveContent) {
    while (current?.parentElement) {
      current = current.parentElement;
      if (current?.classList.contains(tcn.item)) {
        nonInteractive = true;
        break;
      } else if (clickedElement.classList.contains(cn.gridCellContentContainer)) break;
    }
  }
  if (!column.containsInteractiveContent || clickedElement.classList.contains(cn.gridCellContent) || clickedElement.classList.contains(cn.gridCellContentContainer) || nonInteractive) return false;
  return true;
};
export const CellContent = props => {
  const {
    getRowIdentifier,
    getRowIndex,
    showContextArea,
    buildCellBorderStyles,
    selectionMode,
    rowCount,
    setSelectRow,
    selectableOnlyBySelectionControl,
    readOnly
  } = props.itemData;
  const [cellRightMouseClick, setCellRightMouseClick] = useState(false);
  const onClick = useCallback(e => {
    if (readOnly) return;
    const columns = props.itemData.columns;
    const column = columns?.find(column => column.key === props.columnKey);
    const rowKey = getRowIdentifier(props.rowIndex);
    const disabledRow = props.itemData.disabled || !!props.itemData.isRowDisabled?.(rowKey);
    const readOnlyRow = props.itemData.readOnly || !!props.itemData.isRowReadOnly?.(rowKey);
    if (!disabledRow && !readOnlyRow) column?.onPress?.(props.rowIndex, props.columnKey);
    if (column && (isCellControl(e, column, props.itemData.isInteractiveControl, rowKey) || column?.preventToggleSelection)) return;
    const singleSelection = selectionMode === 'single' || selectionMode === 'singleRadio';
    const shouldClearPrevious = (() => {
      if (selectionMode === 'toggle') return 'selectionRange';
      return !singleSelection && (e.metaKey || e.ctrlKey) ? false : true;
    })();
    if (!selectableOnlyBySelectionControl && selectionMode !== 'none' && !disabledRow && !readOnlyRow) {
      props.itemData?.setInteractiveAction?.(true);
      setSelectRow?.({
        index: props.rowIndex,
        selected: getSelected(props.itemData, props.rowIndex),
        selectionType: 'cell',
        clearPrevious: shouldClearPrevious,
        rowCount,
        rangeSelection: singleSelection ? false : e.shiftKey,
        rowKey: getRowIdentifier,
        getRowIndex,
        hideSelectAll: props.itemData.hideSelectAll || props.itemData.selectionMode !== 'multiple' && props.itemData.selectionMode !== 'toggle',
        columnHeadersVisible: props.itemData.columnHeadersVisible,
        areRowsVisible: props.itemData.areRowsVisible,
        isRowDisabled: props.itemData.isRowDisabled,
        isRowReadOnly: props.itemData.isRowReadOnly,
        interactive: true
      });
    }
  }, [readOnly, rowCount, selectionMode, selectableOnlyBySelectionControl, getRowIdentifier, getRowIndex, props.rowIndex, props.columnKey, props.itemData, setSelectRow]);
  const onDoubleClick = useCallback(() => {
    const {
      onDoubleClick
    } = props.itemData;
    onDoubleClick?.({
      rowKey: getRowIdentifier(props.rowIndex),
      rowIndex: props.rowIndex,
      columnKey: props.columnKey
    });
  }, [props.columnKey, props.itemData, props.rowIndex, getRowIdentifier]);
  const disabled = props.itemData.disabled || props.rowIndex > -1 && !!props.itemData.isRowDisabled?.(getRowIdentifier(props.rowIndex));
  const singleAndDoubleClick = useSingleAndDoubleClick({
    persist: true,
    onSingleClick(e) {
      onClick(e);
    },
    onDoubleClick() {
      onDoubleClick();
    }
  });
  const clickHandler = {
    onClick
  };
  if (props.itemData.onDoubleClick) {
    if (props.itemData.disableClickDelay) {
      clickHandler.onDoubleClick = onDoubleClick;
    } else {
      clickHandler.onClick = singleAndDoubleClick.onClick;
      clickHandler.onDoubleClick = singleAndDoubleClick.onDoubleClick;
    }
  }
  const columns = props.itemData.columns;
  const column = columns.find(({
    key
  }) => key === props.columnKey);
  const [isFocusLockDisabled, disableFocusLock] = useState(true);
  const [shouldFocusTableCell, setShouldFocusTableCell] = useState(false);
  const onOutsideClickHandler = () => {
    disableFocusLock(true);
    // NOVACOMMONS-24874: must leave edit state when editted cell content loses focus
    const editState = props.itemData.getEditState();
    if (editState.isEditing && editState.rowIndex === props.rowIndex && editState.column === column) {
      // we were editing this cell, but have now clicked outside and should cancel editing
      props.itemData.cancelEditingCell();
    }
  };
  const cellContentRef = useRef(null);
  const ignoreBlur = useRef(false);
  const keyPressed = useRef(false);
  useOnOutsideMouseDown(cellContentRef, onOutsideClickHandler, true);
  useEffect(() => {
    shouldFocusTableCell && disableFocusLock(true);
  }, [shouldFocusTableCell]);
  const [didBlur, setDidBlur] = useState(false);
  const mounted = useMounted();
  useEffect(() => {
    props.focus && !didBlur && props.contentRef?.current?.focus();
  }, [mounted, props.focus, props.contentRef, didBlur]);
  const pressedCellColIndex = props.itemData.pressedCell?.colIndex;
  const pressedCellColKey = columns && pressedCellColIndex !== undefined && columns[pressedCellColIndex]?.key;
  const pressedCellIsDataOrHeader = props.itemData.pressedCell?.region === TableRegions.Data || props.itemData.pressedCell?.region === TableRegions.RowHeader;
  const pressedCellInSameRow = props.rowIndex === props.itemData.pressedCell?.rowIndex;
  const pressedCellInSameColumn = pressedCellColKey === props.columnKey;
  const cellShouldShowPressedState = pressedCellIsDataOrHeader && pressedCellInSameRow && (props.itemData.pressEntireRow || pressedCellInSameColumn);
  const selectionStateOverride = props.itemData.getSelectionState?.(getRowIdentifier(props.rowIndex));
  const cellSelected = selectionStateOverride !== undefined ? !!selectionStateOverride : props.itemData.preference?.selectAll === true || props.itemData.preference?.selectedRows?.has(getRowIdentifier(props.rowIndex));
  const stopEnterKeyPropagation = event => {
    if (event.key === 'Enter') stopPropagation(event);
  };
  const stopPropagation = event => {
    if (!mounted.current) return;
    const columns = props.itemData.columns;
    const rowKey = getRowIdentifier(props.rowIndex);
    const column = columns?.find(column => column.key === props.columnKey);
    if (event.target !== event.currentTarget && column && isCellControl(event, column, props.itemData.isInteractiveControl, rowKey)) {
      // prevent column's onActivation and table's onDoubleClick due to
      // already handled at the interactive control within a cell
      event.stopPropagation();
    }
  };
  const cellContentDiv = /*#__PURE__*/_jsx("div", {
    ...clickHandler,
    ref: cellContentRef,
    style: {
      ...column?.style,
      textAlign: column?.alignment ?? 'start',
      overflow: 'hidden',
      ...props.style
    },
    onMouseEnter: e => {
      props.itemData.onCellMouseEnter?.(e, props.rowIndex, props.columnKey);
    },
    onMouseDown: e => {
      if (e.shiftKey) e.preventDefault();
      if (e.button === 2) setCellRightMouseClick(true);
    },
    onMouseUp: e => {
      if (e.button === 2) setCellRightMouseClick(false);
    },
    onKeyDown: e => {
      keyPressed.current = true;
      // do nothing if not mounted
      if (!mounted.current) return;

      // F2 toggles the focus lock
      if (e.key === 'F2' && !e.shiftKey && !e.ctrlKey && !e.altKey) {
        ignoreBlur.current = false;
        if (isFocusLockDisabled) disableFocusLock(false);else setShouldFocusTableCell(true);
      }
      // escape deactivates the focus lock and resets focus on the table
      else if (e.key === 'Escape' && !isFocusLockDisabled) {
        ignoreBlur.current = false;
        setShouldFocusTableCell(true);
      }
      // enter activates the focus lock
      else if (e.key === 'Enter' && isFocusLockDisabled) {
        ignoreBlur.current = true;
        disableFocusLock(false);
      }
      // Any key other than escape or enter should not disable the focus lock
      // or move focus out of the control(s) in the cell
      else ignoreBlur.current = true;
    },
    onFocus: event => {
      const targetInTable = event.target.closest(`.${cn.gridCellContentContainer}`) !== null;
      const relatedTargetInTable = event.relatedTarget?.closest(`.${cn.gridCellContentContainer}`) !== null;
      const mouseTargetNotTable = keyPressed.current ? false : !targetInTable;
      // when the cell content gets focus, activate the focus lock
      // but only if the focus is on table cell content
      // otherwise, key presses in popovers for cell controls will toggle focus lock
      if (isFocusLockDisabled && (targetInTable || relatedTargetInTable) && !mouseTargetNotTable) {
        disableFocusLock(false);
      }
      keyPressed.current = false;
      props.itemData.onCellFocus?.(event, props.rowIndex, props.columnKey);
    },
    onBlur: () => {
      // when the cell content loses focus, deactivate the focus lock
      if (!ignoreBlur.current && !isFocusLockDisabled) {
        disableFocusLock(true);
      }
      ignoreBlur.current = false;
      keyPressed.current = false;
    },
    className: cx(props.className, cn.gridCellContentContainer, cellSelected && (!props.itemData.singleSelectionOverrides?.hideStyles || props.itemData.selectionMode !== 'single' && props.itemData.selectionMode !== 'singleRadio') ? props.itemData.dragging === true ? undefined : cn.selected : undefined, cellRightMouseClick && cn.gridCellNoSelection, showContextArea(getRowIdentifier(props.rowIndex), props.columnKey) ? cn.contextArea : undefined, buildCellBorderStyles(props.itemData.getRowIdentifier(props.rowIndex)), column?.className, cellShouldShowPressedState && cn.pressed),
    "data-context-menu-visible": showContextArea(getRowIdentifier(props.rowIndex), props.columnKey),
    children: /*#__PURE__*/_jsx("span", {
      className: cn.gridCellContent,
      onBlur: () => setDidBlur(true),
      onClick: event => stopPropagation(event),
      onKeyDown: event => stopEnterKeyPropagation(event),
      onKeyUp: event => stopEnterKeyPropagation(event),
      children: props.children
    })
  });
  const onFocusLockDeactivation = useCallback(() => {
    // from https://www.npmjs.com/package/react-focus-lock:
    // Trap will be still active by the time you may want move(return) focus
    // on componentWillUnmount. Please defer this action with a zero-timeout.
    // Similarly, if you are using the disabled prop to control FocusLock,
    // you will need a zero-timeout to correctly restore focus.
    setTimeout(() => {
      if (shouldFocusTableCell) props.itemData.refTop.current?.focus();
      if (mounted.current) setShouldFocusTableCell(false);
    }, 0);
  }, [shouldFocusTableCell, mounted, props.itemData.refTop]);
  return /*#__PURE__*/_jsx(ContextMenu, {
    onContextMenu: e => {
      if (!e || disabled || props.itemData.hasContextMenu() !== true) return;
      const columns = props.itemData.columns;
      const column = columns?.find(column => column.key === props.columnKey);
      if (!column) return;
      const rowKey = getRowIdentifier(props.rowIndex);
      const actualColumnIndex = props.itemData.columns.findIndex(c => c.key === column.key);
      const contextMenuInfo = {
        column,
        contextInfo: {
          position: {
            x: e.pageX,
            y: e.pageY
          },
          cell: {
            column: column.key,
            row: rowKey
          }
        },
        position: {
          region: TableRegions.ColumnHeader,
          rowIndex: props.rowIndex,
          colIndex: actualColumnIndex
        }
      };
      props.itemData.setContextMenuInfo(contextMenuInfo);
      props.itemData.updateContextMenuItems(props.itemData.cellContextMenu, {
        rowKey: rowKey || '',
        columnKey: props.columnKey
      });
      props.itemData.setContextMenuOpen(true);
      props.itemData.storePreviousFocusIndicator(props.itemData.focusIndicator);
    },
    disabled: disabled,
    children: !column?.containsInteractiveContent ? cellContentDiv : /*#__PURE__*/_jsx(FocusLock, {
      disabled: isFocusLockDisabled,
      onDeactivation: onFocusLockDeactivation,
      className: cn.gridCellContentFocuslock,
      children: cellContentDiv
    })
  });
};
//# sourceMappingURL=CellContent.js.map